import { useEffect, useState } from 'react';
import { getEmailQueue, retryFailedEmailQueue, retrySelectedEmailQueue } from '../../../api/adminEmailCenter';
import EmailCenterPage, { EmptyState, PageSection, StatCard, StatusBadge, formatDateTime } from './EmailCenterShared';

export default function EmailQueue() {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const data = await getEmailQueue();
      setState({ loading: false, error: '', data });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load queue' }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const retryFailed = async () => {
    setBusy(true);
    try {
      await retryFailedEmailQueue();
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message || 'Failed to retry failed messages' }));
    } finally {
      setBusy(false);
    }
  };

  const retrySelected = async () => {
    const ids = Object.entries(selected)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id));
    if (!ids.length) return;
    setBusy(true);
    try {
      await retrySelectedEmailQueue({ ids });
      setSelected({});
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message || 'Failed to retry selected messages' }));
    } finally {
      setBusy(false);
    }
  };

  const queue = state.data?.summary || {};

  return (
    <EmailCenterPage
      title="Queue & Retry"
      description="Monitor live queue pressure, recent pending items, worker heartbeat, and safe retry controls for failed delivery."
      actions={
        <>
          <button type="button" onClick={retrySelected} disabled={busy} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
            Retry Selected
          </button>
          <button type="button" onClick={retryFailed} disabled={busy} className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,118,110,0.24)]">
            Retry Failed
          </button>
        </>
      }
    >
      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}

      <div className="grid gap-4 xl:grid-cols-5 md:grid-cols-2">
        <StatCard label="Queued" value={queue.queued || 0} tone="amber" />
        <StatCard label="Processing" value={queue.processing || 0} tone="sky" />
        <StatCard label="Failed" value={queue.failed || 0} tone="rose" />
        <StatCard label="Sent" value={queue.sent || 0} tone="emerald" />
        <StatCard label="Last Worker Run" value={queue.lastWorkerRunAt ? 'OK' : 'N/A'} hint={queue.lastWorkerRunAt ? `${queue.lastWorkerStatus || 'unknown'} • ${formatDateTime(queue.lastWorkerRunAt)}` : 'Worker metadata unavailable'} />
      </div>

      <PageSection title="Pending / Failed Items" subtitle="Operational queue view for targeted retries without duplicating already-sent emails.">
        {!state.data?.items?.length && !state.loading ? (
          <EmptyState title="Queue is clear" body="No queued, processing, or failed messages are waiting for action." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.24em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Pick</th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {(state.data?.items || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={Boolean(selected[row.id])}
                        disabled={!['failed', 'rejected'].includes(row.status)}
                        onChange={(e) => setSelected((current) => ({ ...current, [row.id]: e.target.checked }))}
                      />
                    </td>
                    <td className="px-4 py-4 text-slate-600">{row.recipientEmail}</td>
                    <td className="px-4 py-4 text-slate-700">{row.subject}</td>
                    <td className="px-4 py-4">
                      <StatusBadge value={row.status} />
                    </td>
                    <td className="px-4 py-4 text-slate-600">{row.attemptCount}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>
    </EmailCenterPage>
  );
}
